import { useTheme } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
//import createConversationStream from "Services/ConversationStream";
import { IMessage } from "src/Common/Interfaces/Domain/Conversations/IMessages";
import { IConversation } from "Common/Interfaces/Domain/Conversations/IConversation";
import { IRawResponse } from "Common/Interfaces/Domain/Conversations/IRawResponse";
import { IConversationList, ICreateConversationRequest } from "Common/Interfaces/Domain/Conversations";
import { useAddFeedbackMutation, useGetConversationHistoryQuery, useLazyGetConversationQuery } from "Services/Conversations";
import { USER_MESSAGE_TYPES } from "Common/Constants/UserMessageTypes";
import { useLocation, useParams } from "react-router-dom";
import { IChatWindow } from "../Interfaces";
import { useMatchDownMd } from "Common/Hooks";
import { IErrorDetail, IFeedback, IUIElementState } from "Common/Interfaces";
import { INFO_TYPE, SNACKBAR_SEVERITY, UI_ELEMENTS } from "Common/Constants";
import { useDispatch } from "react-redux";
import { AppDispatch } from "src/Store";
import { setConversationLoading } from "src/Store/Slice";
import useConversationStream from "Services/ConversationStream";

export const useChatWindow = (props: IChatWindow) => {
  //#region State declartions-----------------------------------------
  const { isInitial } = props;
  const theme = useTheme();
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [conversation, setConversation] = useState<IConversation | undefined>(undefined);
  const [conversationId, setConversationId] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [conversationList, setConversationList] = useState<IConversationList>();
  const location = useLocation();
  const { chatId } = useParams();

  // Create a ref for the scrollable container
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Ref to track if conversation is loading (for scroll handler to access without re-creating)
  const isConversationLoadingRef = useRef<boolean>(false);
  // Ref to track the latest conversationId from streaming (avoids closure staleness)
  const latestConversationIdRef = useRef<string | undefined>(undefined);
  const { matchDownMd } = useMatchDownMd();
  // State to track if we should auto-scroll or not
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isCollapsedReference, setIsCollapsedReference] = useState(true);
  const [referenceDrawer, setReferenceDrawer] = useState(false);
  const [activeUrl, setActiveUrl] = useState<string | undefined>(undefined);
  const [pageNum, setPageNum] = useState<number>(1);
  const [addFeedback] = useAddFeedbackMutation();
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [dialogResolve, setDialogResolve] = useState<((comment: string) => void) | null>(null); // Allow null initially
  const [paragraphs, setParagraphs] = useState<string | undefined>(undefined);
  const [pageResetKey, setPageResetKey] = useState<number | undefined>(undefined);
  const [infoType, setInfoType] = useState<INFO_TYPE>(INFO_TYPE.DOCUMENT);

  const dispatch = useDispatch<AppDispatch>();

  const { createConversationStream } = useConversationStream();
  //#endregion

  useEffect(() => {
    if (matchDownMd) {
      setReferenceDrawer(false);
      setIsCollapsed(true);
      setIsCollapsedReference(true);
      // Clear reference state when going to mobile so that clicking
      // the same reference again triggers a fresh state change
      resetReference();
    }
  }, [matchDownMd, location]);

  useEffect(() => {
    if (infoType === INFO_TYPE.SERVICE_HISTORY) {
      setIsCollapsedReference(true);
    }
  }, [infoType]);

  const [
    trigger,
    { data, error: conversationError, isLoading: isConversationMessagesLoading, isFetching: isConversationMessagesFetching },
  ] = useLazyGetConversationQuery();

  const {
    data: conversations,
    isLoading: isConversationsLoading,
    refetch,
  } = useGetConversationHistoryQuery({ pageSize: 100, continuationToken: undefined });

  const [uiElementState, setUIElementState] = useState<IUIElementState<IFeedback>>({
    anchorEl: null,
    activeElement: null,
    dataRow: null,
  });

  const handleCloseUIElement = () => {
    setUIElementState((prevState) => ({
      ...prevState,
      activeElement: null,
      anchorEl: null,
    }));
  };

  const [editingMessage, setEditingMessage] = useState<string | null>(null);
  const [editedText, setEditedText] = useState<string>("");

  const handleEditMessage = (messageKey: string) => {
    const message = messages.find((msg) => msg.key === messageKey);
    if (message && message.user === "me") {
      setEditingMessage(messageKey);
      setEditedText(message.text);
    }
  };
  useEffect(() => {
    if (conversationId || conversationId !== "") {
      trigger(conversationId);
    }
  }, [conversationId]);

  useEffect(() => {
    setConversationList(conversations);
  }, [conversations]);

  const handleSaveEdit = (messageKey: string) => {
    setMessages(messages.map((msg) => (msg.key === messageKey ? { ...msg, text: editedText } : msg)));
    setEditingMessage(null);
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setEditedText("");
  };
  const [isConversationProcessing, setIsConversationProcessing] = useState<boolean>(false);

  const handleGetConversation = useCallback(async (conversationId: string | undefined) => {
    setIsConversationProcessing(true);
    if (conversationId && conversationId !== "") {
      setConversationId(conversationId);
    } else {
      setConversation(undefined);
      setIsConversationProcessing(false);
    }
  }, []);

  useEffect(() => {
    if (!isConversationMessagesLoading && !conversationError) {
      setConversation(data);
      setIsConversationProcessing(false);
      if (data?.infoRequested) {
        setInfoType(data.infoRequested);
      }
    }
    if (conversationError) {
      const message: IMessage = { text: "Failed to load conversation.", key: "0", user: USER_MESSAGE_TYPES.ERROR };
      const errorMessage: IConversation = { title: undefined, messages: [message], conversationId: undefined };
      setConversation(errorMessage);
      setIsConversationProcessing(false);
    }
  }, [conversationError, data, isConversationMessagesLoading]);

  const [isConversationLoading, setIsConversationLoading] = useState<boolean>(false); // Changed loading state name
  const [showFeedbackButtons, setShowFeedbackButtons] = useState<boolean>(true); // Changed loading state name

  // Keep the ref in sync with the loading state for scroll handler
  useEffect(() => {
    isConversationLoadingRef.current = isConversationLoading;
  }, [isConversationLoading]);

  //#region Division Scope Logic ----------------------------------------------------

  const rawScope = conversation?.divisionScope;
  const divisionScope = rawScope && rawScope !== "None" ? rawScope : undefined;
  //#endregion -------------------------------------------------------------------------

  //#region Send Message and handling -----------------------------------------
  const getParentMessageId = () => {
    if (conversation?.messages === undefined) {
      return undefined;
    } else {
      const lastMessage = conversation?.messages[conversation?.messages.length - 1];
      return lastMessage?.key;
    }
  };

  const handleSendMessage = async (request: ICreateConversationRequest) => {
    dispatch(setConversationLoading(true));
    setShouldAutoScroll(true);
    scrollToBottom();
    try {
      setError(null);
      const parentKey = getParentMessageId();
      await createConversationStream(
        {
          content: request.content,
          parentId: parentKey,
          conversationId: request.conversationId,
          authorRole: "USER",
          infoType: infoType,
        },
        handleStreamResponse,
      );
    } catch (err) {
      handleError(err);
    } finally {
      finalizeConversation();
    }
  };

  const appendMessageToConversation = (message: IMessage) => {
    setConversation((prevConversation) => ({
      ...prevConversation,
      messages: [...(prevConversation?.messages || []), message],
    }));
  };

  const handleStreamResponse = (response: IRawResponse | string) => {
    setIsConversationLoading(true);
    setShowFeedbackButtons(false);
    setConversation((prevConversation) => {
      if (typeof response === "string") {
        return updateConversationTitle(prevConversation, response);
      } else {
        return handleMessageResponse(prevConversation, response);
      }
    });
  };

  const updateConversationTitle = (prevConversation: IConversation | undefined, title: string) => ({
    ...prevConversation,
    title,
  });

  const handleMessageResponse = (prevConversation: IConversation | undefined, response: IRawResponse) => {
    const newConversationId = prevConversation?.conversationId ?? response.conversationId;
    // Update the ref with the latest conversationId so finalizeConversation can access it
    latestConversationIdRef.current = newConversationId;
    const existingMessageIndex = findExistingMessageIndex(prevConversation, response.messageId);
    if (existingMessageIndex === -1 || existingMessageIndex === undefined) {
      const newResponseMessage = createNewResponseMessage(response);
      return appendResponseMessage(prevConversation, newResponseMessage, newConversationId);
    } else {
      return updateExistingMessage(prevConversation, response, existingMessageIndex, newConversationId);
    }
  };

  const findExistingMessageIndex = (prevConversation: IConversation | undefined, messageId: string) =>
    prevConversation?.messages?.findIndex((msg: IMessage) => msg.key === messageId);

  const createNewResponseMessage = (response: IRawResponse): IMessage => {
    let userType;
    if (response.authorRole === "USER") {
      userType = USER_MESSAGE_TYPES.USER;
    } else if (response.authorRole === "SYSTEM") {
      userType = USER_MESSAGE_TYPES.SYSTEM;
    } else {
      userType = USER_MESSAGE_TYPES.ASSISTANT;
    }
    return {
      key: response.messageId,
      text: response.content,
      user: userType,
    };
  };

  const appendResponseMessage = (prevConversation: IConversation | undefined, message: IMessage, conversationId: string) => {
    return {
      ...prevConversation,
      conversationId,
      messages: [...(prevConversation?.messages || []), message],
    };
  };

  const updateExistingMessage = (
    prevConversation: IConversation | undefined,
    response: IRawResponse,
    index: number,
    conversationId: string,
  ) => {
    const updatedMessages = [...(prevConversation?.messages || [])];
    updatedMessages[index].text = response.content;
    return {
      ...prevConversation,
      conversationId,
      messages: updatedMessages,
    };
  };

  const handleError = (err: unknown) => {
    const errorMessage: IMessage = {
      key: `error-${Date.now()}`,
      text:
        err instanceof Error ? `Failed to send message: ${err.message}` : "Failed to send message due to an unknown error.",
      user: USER_MESSAGE_TYPES.ERROR,
    };

    appendMessageToConversation(errorMessage);
  };

  const finalizeConversation = () => {
    setIsConversationLoading(false);
    setShowFeedbackButtons(true);
    // Silently refetch to get updated divisionScope - the ref avoids stale closure issue
    const conversationIdToRefetch = latestConversationIdRef.current;
    if (conversationIdToRefetch) {
      trigger(conversationIdToRefetch);
    }
  };

  useEffect(() => {
    refetch();
  }, [conversation?.conversationId, conversation?.title, refetch]);

  const [hoveredMessageKey, setHoveredMessageKey] = useState<string | null>(null);
  //#endregion--------------------------------------------------------------------

  //#region References handling --------------------------------------------------------------
  const resetReference = () => {
    setActiveUrl(undefined);
    setParagraphs(undefined);
  };

  useEffect(() => {
    if (isCollapsed) {
      resetReference();
    }
  }, [isCollapsed]);

  useEffect(() => {
    if (isCollapsedReference) {
      // Clear reference state when the reference panel is collapsed so that
      // clicking the same document reference again triggers a fresh state change
      resetReference();
    }
  }, [isCollapsedReference]);

  useEffect(() => {
    // Reset auto-scroll when switching chats
    setShouldAutoScroll(true);
    // Reset the conversationId ref when switching chats to prevent stale values
    latestConversationIdRef.current = chatId;

    if (chatId && chatId !== "") {
      setIsCollapsedReference(true);
      handleGetConversation(chatId);
    }
    if (chatId === undefined || chatId === "") {
      setInfoType(INFO_TYPE.DOCUMENT);
      setIsCollapsedReference(true);
    }
  }, [chatId, isInitial, handleGetConversation]);

  useEffect(() => {
    if (chatId && conversation === undefined) {
      //to prevent initial chat view showing briefly when loading a previous chat conversation.
      setIsConversationProcessing(true);
    }
  }, [chatId, conversation]);

  // Memoized scroll function
  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current && shouldAutoScroll) {
      // Use requestAnimationFrame to ensure DOM has updated before scrolling
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
      });
    }
  }, [shouldAutoScroll]);

  // Scroll to the bottom when conversation or messages change
  useEffect(() => {
    if (conversation?.messages?.length || !isConversationMessagesLoading) {
      scrollToBottom(); // <- Trigger scroll when messages change
    }
  }, [conversation, isConversationMessagesLoading, scrollToBottom, isConversationLoading]);

  // Function to detect if the user has scrolled up
  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;

      // If the user is at (or very close to) the bottom, enable auto-scrolling again
      // Using a small threshold (5px) to account for sub-pixel rendering differences
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 5;
      const isLoading = isConversationLoadingRef.current;

      if (isAtBottom) {
        setShouldAutoScroll(true);
      } else if (!isLoading) {
        // Only disable auto-scroll if the user scrolled up AND the bot is NOT streaming
        // This prevents false "scroll up" detection caused by content height changes during streaming
        setShouldAutoScroll(false);
      }
      // If isLoading is true and user is not at bottom, keep shouldAutoScroll as is
      // (don't disable it due to scroll position fluctuations during streaming)
    }
  }, []);

  // Attach the scroll event listener to the container
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isConversationMessagesFetching, conversation?.conversationId, handleScroll]);
  const handleLinkClick = (
    href: string | undefined,
    pageNumber: number,
    event?: React.MouseEvent<HTMLAnchorElement>,
    paragraph?: string,
  ) => {
    if (!href) {
      return; // Exit if href is undefined
    }

    if (event) {
      event.preventDefault();
    }
    if (href.includes(import.meta.env.VITE_DOCUMENT_URL)) {
      if (isConversationLoading) {
        // If the chat is loading, return early and notify the user.
        // References cannot load while the conversation is still loading.
        handleOpenSnackbar("References are loading. Please try after the chat is loaded.", false, true);
        setPageNum(0);
        setParagraphs(undefined);
        return;
      }

      setActiveUrl(href);
      setPageResetKey(Math.random());
      setPageNum(pageNumber);

      if (matchDownMd) {
        setReferenceDrawer(true);
      } else {
        setIsCollapsedReference(false);
      }

      setParagraphs(paragraph);
    } else {
      // Open in a new tab
      window.open(href, "_blank", "noopener,noreferrer");
    }
  };

  //#endregion --------------------------------------------

  //#region Function to handle sending message -------------------------
  const getRequestMessage = (requestMessage: string) => {
    const request: ICreateConversationRequest = {
      content: requestMessage,
      conversationId: conversation?.conversationId,
      infoType: infoType,
    };
    handleSendMessage(request);
  };

  useEffect(() => {
    if (conversation?.conversationId) {
      const newUrl = `/c/${conversation.conversationId}`;

      window.history.replaceState(window.history.state, conversation.title ? conversation.title : "New Chat", newUrl);
      resetReference();
      setIsCollapsed(true);
    }
  }, [conversation]);

  useEffect(() => {
    if (location.state?.id) {
      setConversation(undefined);
      setInfoType(INFO_TYPE.DOCUMENT);
    }
  }, [location.state, setConversation]);
  //#endregion -------------------------------

  //#region Snackbar ----------------------------
  const handleOpenSnackbar = (message: string, isSuccess: boolean, isWarning?: boolean) => {
    setUIElementState((prevState) => ({
      ...prevState,
      anchorEl: null,
      activeElement: UI_ELEMENTS.SNACKBAR,
      snackbarMessage: message,
      snackbarSeverity: isSuccess
        ? SNACKBAR_SEVERITY.SUCCESS
        : isWarning
          ? SNACKBAR_SEVERITY.WARNING
          : SNACKBAR_SEVERITY.ERROR,
    }));
  };
  //endregion -----------------------------------------------------------

  //#region Feedback --------------------------------------------------------
  const [submitFeedback, setSubmitFeedback] = useState<IFeedback>({
    conversationId: "",
    messageId: "",
    thumbs: false,
    comment: "",
  });
  const [selectedFeedback, setSelectedFeedback] = useState<{ messageId: string; type: "up" | "down" | null }[]>([]);
  const handlePositiveFeedback = (
    conversationId: string,
    messageId: string,
    thumbs: boolean,
    comment: string | undefined,
  ) => {
    if (conversationId && messageId) {
      addFeedback({ conversationId, messageId, thumbs, comment })
        .unwrap()
        .then(() => {
          handleOpenSnackbar("Feedback submitted successfully!", true);
          setSubmitFeedback({
            conversationId: conversationId,
            messageId: messageId,
            thumbs: true,
            comment: "",
          });
          setSelectedFeedback((prev) => [...prev, { messageId, type: "up" }]);
        })
        .catch((err: IErrorDetail) => {
          handleOpenSnackbar(err.message, false);
        });
    }
  };

  const openFeedbackDialog = (): Promise<string> => {
    return new Promise((resolve) => {
      setDialogResolve(() => resolve);
      setIsDialogOpen(true);
    });
  };

  const handleNegativeFeedback = async (conversationId: string, messageId: string, thumbs: boolean) => {
    const comment = await openFeedbackDialog();
    if (conversationId && messageId) {
      addFeedback({ conversationId, messageId, thumbs, comment })
        .unwrap()
        .then(() => {
          handleOpenSnackbar("Feedback submitted successfully!", true);
          setSubmitFeedback({
            conversationId: conversationId,
            messageId: messageId,
            thumbs: false,
            comment: comment,
          });
          setSelectedFeedback((prev) => [...prev, { messageId, type: "down" }]);
        })
        .catch((err: IErrorDetail) => {
          handleOpenSnackbar(err.message, false);
        });
    }
  };

  const handleSubmitComment = (comment: string) => {
    if (dialogResolve) {
      dialogResolve(comment);
    }
    setIsDialogOpen(false);
  };
  //#endregion---------------------------------------------------------

  useEffect(() => {
    if (conversations && conversations.conversations.length > 0) {
      if (
        conversations?.conversations[0].conversationId &&
        conversations?.conversations[0].conversationId !== conversationId
      ) {
        refetch();
      }
    }
  }, [isConversationLoading]);

  useEffect(() => {
    dispatch(setConversationLoading(isConversationLoading));
  }, [isConversationLoading]);

  return {
    theme,
    submitFeedback,
    selectedFeedback,
    conversation,
    messages,
    editingMessage,
    editedText,
    handleEditMessage,
    handleSaveEdit,
    handleCancelEdit,
    isConversationMessagesFetching,
    handleGetConversation,
    handleSendMessage,
    hoveredMessageKey,
    setHoveredMessageKey,
    isConversationMessagesLoading,
    conversationId,
    setConversation,
    error,
    conversationList,
    isConversationsLoading,
    isConversationLoading,
    handleLinkClick,
    handlePositiveFeedback,
    handleNegativeFeedback,
    setIsCollapsedReference,
    isCollapsedReference,
    scrollContainerRef,
    getRequestMessage,
    activeUrl,
    pageNum,
    resetReference,
    paragraphs,
    isDialogOpen,
    setIsDialogOpen,
    handleSubmitComment,
    matchDownMd,
    referenceDrawer,
    setReferenceDrawer,
    handleCloseUIElement,
    uiElementState,
    showFeedbackButtons,
    setIsConversationLoading,
    setSelectedFeedback,
    pageResetKey,
    handleOpenSnackbar,
    isConversationProcessing,

    infoType,
    setInfoType,
    divisionScope,
    isDivisionLoading: isConversationProcessing || isConversationMessagesLoading,
  };
};
